// Web shim for @op-engineering/op-sqlite
// 使用 sql.js 提供真正的 SQLite 数据库支持

// sql.js 类型
type SQLDatabase = {
  run(sql: string, params?: any[]): void;
  exec(sql: string, params?: any[]): QueryExecResult[];
  prepare(sql: string): SQLStatement;
  export(): Uint8Array;
  close(): void;
  getRowsModified(): number;
};

type SQLStatement = {
  bind(params: any[]): void;
  step(): boolean;
  get(): any[];
  getAsObject(): any;
  free(): void;
};

type QueryExecResult = {
  columns: string[];
  values: any[][];
};

type SqlJsStatic = {
  Database: new (data?: Uint8Array) => SQLDatabase;
};

// 动态导入 sql.js
const initSqlJs = require('sql.js');

// 优先使用 webpack 打包后的本地 wasm 资源，避免外网/CDN 返回 HTML 导致加载失败
let localSqlWasmUrl: string | null = null;
try {
  const resolved = require('sql.js/dist/sql-wasm.wasm');
  localSqlWasmUrl = typeof resolved === 'string' ? resolved : (resolved?.default ?? null);
} catch (err) {
  localSqlWasmUrl = null;
}

// 类型声明
declare const window: any;

export type OPSQLiteConnection = any;
export type SQLBatchTuple = [string, any[]];
export type BatchQueryResult = {
  error?: any;
  batchResult?: any[];
};

// IndexedDB 存储键前缀
const IDB_PREFIX = 'sqlite_db_';

// 全局 SQL.js 实例
let SQL: any = null;
let sqlJsInitPromise: Promise<any> | null = null;

// 初始化 sql.js
async function initSQL(): Promise<any> {
  if (SQL) {
    return SQL;
  }
  
  if (sqlJsInitPromise) {
    return sqlJsInitPromise;
  }
  
  sqlJsInitPromise = initSqlJs({
    locateFile: (file: string) => {
      // sql.js 需要加载 .wasm 文件
      // 优先从本地打包资源加载，失败时再回退 CDN
      if (file.endsWith('.wasm')) {
        if (localSqlWasmUrl) {
          return localSqlWasmUrl;
        }
        return `https://sql.js.org/dist/${file}`;
      }
      return file;
    }
  }).then((sql: any) => {
    SQL = sql;
    console.log('✅ [SQL.js] 初始化成功');
    return sql;
  }).catch((err: any) => {
    console.error('❌ [SQL.js] 初始化失败:', err);
    sqlJsInitPromise = null;
    throw err;
  });
  
  return sqlJsInitPromise;
}

// IndexedDB 工具函数
async function loadFromIndexedDB(dbName: string): Promise<ArrayBuffer | null> {
  if (typeof window === 'undefined' || !window.indexedDB) {
    return null;
  }
  
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open('SQLiteDBs', 1);
    
    request.onerror = () => {
      console.error('❌ [IndexedDB] 打开失败');
      resolve(null);
    };
    
    request.onsuccess = (event: any) => {
      const db = event.target.result;
      const transaction = db.transaction(['databases'], 'readonly');
      const store = transaction.objectStore('databases');
      const getRequest = store.get(dbName);
      
      getRequest.onsuccess = () => {
        if (getRequest.result) {
          console.log(`📥 [IndexedDB] 加载数据库: ${dbName}`, getRequest.result.data.byteLength, 'bytes');
          resolve(getRequest.result.data);
        } else {
          console.log(`📥 [IndexedDB] 数据库不存在: ${dbName}`);
          resolve(null);
        }
      };
      
      getRequest.onerror = () => {
        console.error('❌ [IndexedDB] 读取失败');
        resolve(null);
      };
    };
    
    request.onupgradeneeded = (event: any) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('databases')) {
        db.createObjectStore('databases', { keyPath: 'name' });
      }
    };
  });
}

async function saveToIndexedDB(dbName: string, data: ArrayBuffer): Promise<void> {
  if (typeof window === 'undefined' || !window.indexedDB) {
    return;
  }
  
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open('SQLiteDBs', 1);
    
    request.onerror = () => {
      console.error('❌ [IndexedDB] 打开失败');
      reject(new Error('IndexedDB open failed'));
    };
    
    request.onsuccess = (event: any) => {
      const db = event.target.result;
      const transaction = db.transaction(['databases'], 'readwrite');
      const store = transaction.objectStore('databases');
      const putRequest = store.put({ name: dbName, data });
      
      putRequest.onsuccess = () => {
        console.log(`💾 [IndexedDB] 保存数据库: ${dbName}`, data.byteLength, 'bytes');
        resolve();
      };
      
      putRequest.onerror = () => {
        console.error('❌ [IndexedDB] 保存失败');
        reject(new Error('IndexedDB save failed'));
      };
    };
    
    request.onupgradeneeded = (event: any) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('databases')) {
        db.createObjectStore('databases', { keyPath: 'name' });
      }
    };
  });
}

// SQLite 连接实现
class OPSQLiteConnectionImpl {
  private dbName: string;
  private db: SQLDatabase | null = null;
  private isInitialized: boolean = false;
  private initPromise: Promise<void> | null = null;
  private saveTimer: NodeJS.Timeout | null = null;
  private pendingSave: boolean = false;

  constructor(dbName: string = 'default') {
    this.dbName = dbName;
    this.init();
  }

  private async init(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    
    if (this.initPromise) {
      return this.initPromise;
    }
    
    this.initPromise = (async () => {
      try {
        // 初始化 sql.js
        const SQL = await initSQL();
        
        // 从 IndexedDB 加载数据库
        const savedData = await loadFromIndexedDB(this.dbName);
        
        if (savedData) {
          // 从保存的数据恢复数据库
          this.db = new SQL.Database(new Uint8Array(savedData));
          console.log(`✅ [SQLite] 从 IndexedDB 恢复数据库: ${this.dbName}`);
        } else {
          // 创建新数据库
          this.db = new SQL.Database();
          console.log(`✅ [SQLite] 创建新数据库: ${this.dbName}`);
        }
        
        this.isInitialized = true;
      } catch (err) {
        console.error(`❌ [SQLite] 初始化失败: ${this.dbName}`, err);
        // 即使失败也创建一个内存数据库，避免阻塞
        try {
          const SQL = await initSQL();
          this.db = new SQL.Database();
          this.isInitialized = true;
        } catch (e) {
          console.error('❌ [SQLite] 创建内存数据库也失败', e);
        }
      }
    })();
    
    return this.initPromise;
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.init();
    }
    if (!this.db) {
      throw new Error('Database not initialized');
    }
  }

  // 防抖保存：避免频繁保存影响性能
  private scheduleSave(): void {
    this.pendingSave = true;
    
    // 清除之前的定时器
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }
    
    // 500ms 内的多次操作只保存一次
    this.saveTimer = setTimeout(() => {
      this.pendingSave = false;
      this.saveDatabase().catch(err => {
        console.error('❌ [SQLite] 延迟保存失败:', err);
      });
    }, 500);
  }
  
  // 立即保存（用于批量操作后）
  private async saveDatabase(): Promise<void> {
    if (!this.db) {
      return;
    }
    
    // 清除定时器
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    
    try {
      const data = this.db.export();
      // 确保是 ArrayBuffer 类型（避免使用 SharedArrayBuffer，因为浏览器环境可能不支持）
      // 直接使用 Uint8Array 创建新的 ArrayBuffer，避免 SharedArrayBuffer 问题
      const uint8Array = new Uint8Array(data);
      const buffer: ArrayBuffer = uint8Array.buffer;
      await saveToIndexedDB(this.dbName, buffer);
    } catch (err) {
      console.error('❌ [SQLite] 保存数据库失败:', err);
      // 不抛出错误，避免影响主流程
    }
  }

  async executeAsync(sql: string, params: any[] = []): Promise<{
    rows: {
      length: number;
      item: (index: number) => any;
      _array: any[];
    };
    rowsAffected: number;
    insertId: number;
  }> {
    await this.ensureInitialized();
    
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    try {
      const sqlLower = sql.trim().toLowerCase();
      const isSelect = sqlLower.startsWith('select');
      
      // sql.js 的参数绑定：使用 prepare + bind，或者直接替换 ? 为值
      // 为了简化，我们直接替换 ? 占位符为实际值（对于简单查询）
      // 对于复杂查询，使用 prepare + bind
      
      if (params && params.length > 0) {
        // 处理参数：将 undefined 转换为 null（sql.js 不支持 undefined）
        const processedParams = params.map(param => param === undefined ? null : param);
        
        // 方法1：使用 prepare + bind（更安全，支持所有 SQL 类型）
        // 将 ? 替换为 $1, $2, ... 格式
        let paramIndex = 1;
        const sqlWithParams = sql.replace(/\?/g, () => `$${paramIndex++}`);
        
        if (isSelect) {
          // SELECT 查询：使用 prepare + bind，然后 step 获取结果
          const stmt = this.db.prepare(sql);
          stmt.bind(processedParams);
          
          const rows: any[] = [];
          while (stmt.step()) {
            const row = stmt.getAsObject();
            rows.push(row);
          }
          
          stmt.free();
          
          return {
            rows: {
              length: rows.length,
              item: (i: number) => rows[i] || null,
              _array: rows,
            },
            rowsAffected: 0,
            insertId: 0,
          };
        } else {
          // INSERT/UPDATE/DELETE：使用 prepare + bind
          const stmt = this.db.prepare(sql);
          stmt.bind(processedParams);
          
          // 执行
          stmt.step();
          
          // 获取影响的行数和插入 ID
          const rowsAffected = this.db.getRowsModified() || 0;
          let insertId = 0;
          try {
            const idResult = this.db.exec("SELECT last_insert_rowid()");
            if (idResult.length > 0 && idResult[0].values.length > 0) {
              insertId = idResult[0].values[0][0] || 0;
            }
          } catch (e) {
            // 忽略错误
          }
          
          stmt.free();
          
          const result = {
            rows: {
              length: 0,
              item: () => null,
              _array: [],
            },
            rowsAffected,
            insertId,
          };
          
          // 延迟保存（防抖）
          this.scheduleSave();
          
          return result;
        }
      } else {
        // 无参数的查询，直接使用 exec
        const results = this.db.exec(sql);
        
        let rows: any[] = [];
        if (results.length > 0) {
          const columns = results[0].columns;
          const values = results[0].values;
          rows = values.map((row: any[]) => {
            const obj: any = {};
            columns.forEach((col: string, idx: number) => {
              obj[col] = row[idx];
            });
            return obj;
          });
        }
        
        const result = {
          rows: {
            length: rows.length,
            item: (i: number) => rows[i] || null,
            _array: rows,
          },
          rowsAffected: this.db.getRowsModified() || 0,
          insertId: 0,
        };
        
        // 如果不是 SELECT，延迟保存（防抖）
        if (!isSelect) {
          this.scheduleSave();
        }
        
        return result;
      }
    } catch (err) {
      console.error('❌ [SQLite] 执行 SQL 失败:', sql, err);
      throw err;
    }
  }

  async executeBatchAsync(commands: SQLBatchTuple[]): Promise<BatchQueryResult> {
    await this.ensureInitialized();
    
    if (!this.db) {
      return { error: 'Database not initialized' };
    }

    const batchResult: any[] = [];
    
    try {
      // sql.js 不支持事务，但我们可以手动处理
      for (const [sql, params] of commands) {
        const result = await this.executeAsync(sql, params || []);
        batchResult.push(result);
      }
      
      // 批量执行后立即保存（重要操作）
      await this.saveDatabase();
      
      return {
        error: null,
        batchResult,
      };
    } catch (err) {
      console.error('❌ [SQLite] 批量执行失败:', err);
      return {
        error: err,
        batchResult,
      };
    }
  }

  close(): void {
    if (this.db) {
      // 清除定时器
      if (this.saveTimer) {
        clearTimeout(this.saveTimer);
        this.saveTimer = null;
      }
      
      // 如果有待保存的操作，立即保存
      if (this.pendingSave) {
        this.saveDatabase().catch(err => {
          console.error('❌ [SQLite] 关闭时保存失败:', err);
        });
      }
      
      this.db.close();
      this.db = null;
      this.isInitialized = false;
      console.log(`🔒 [SQLite] 关闭数据库: ${this.dbName}`);
    }
  }
}

// 导出 open 函数
export async function open(options: {
  name: string;
  location?: string;
}): Promise<OPSQLiteConnection> {
  const dbName = options.name || 'default';
  return new OPSQLiteConnectionImpl(dbName);
}

// 默认导出
export default {
  open,
};
